import React, { useEffect, useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import InfiniteScroll from "react-infinite-scroll-component";
import { Icon } from "@mui/material";
import moment from "moment";

import { AvatarChat } from "Modules/chat/.globals/helpers";
import ChatThread from "Modules/chat/views/chatThread";
import ChatsListSkeleton from "Modules/chat/views/chatsList/components/ChatsListSkeleton";
import ChatsListItem from "Modules/chat/views/chatsList/components/ChatsListItem";
import {
  openChatThread,
  sendChatMessage,
  updateChatMessageStatus,
  close as closeTeamChatList,
} from "../redux/listSlice";
import {
  updateChatChannelMessagesStatus
} from "Modules/chat/.globals/redux/actions";
import EmptyPlaceholder from './../../../../components/EmptyPlaceholder';
import {
  openChatList,
} from "Modules/chat/.globals/redux/actions";
import { NotificationManager } from 'Components/Notifications';

const TeamChatsList = ({
  orderFilterData = [],
  total = 0,
  getListData = () => { },
  authId,
  chatUsers = [],
  messages = {},
  locale = 'en',
  isLoading,
  style
}) => {
  const dispatch = useDispatch();
  const scrollableDivRef = useRef(null);
  const isDraggingRef = useRef(false);
  
  // Memoize event handlers to prevent recreation on every render
  const handlecloseTeamChatList = useCallback(() => {
    dispatch(closeTeamChatList());
  }, [dispatch]);
  
  const handleOpenChat = useCallback((chatId) => {
    dispatch(openChatThread({ chatId, authId }));
  }, [dispatch, authId]);

  const selectedChat = useSelector((state) => state.teamChatListRedux.selectedChat);
  
  // Memoize computed values
  const showEmptyPlaceholder = useMemo(() => 
    !isLoading && !orderFilterData.length && !selectedChat.openModal,
    [isLoading, orderFilterData.length, selectedChat.openModal]
  );

  // Handle pull-to-refresh with proper cleanup on mouse/touch leave
  useEffect(() => {
    const scrollableDiv = scrollableDivRef.current;
    if (!scrollableDiv) return;

    const handleStart = (e) => {
      if (scrollableDiv.scrollTop === 0) {
        isDraggingRef.current = true;
      }
    };

    const handleEnd = () => {
      isDraggingRef.current = false;
    };

    const handleLeave = () => {
      if (isDraggingRef.current) {
        // Simulate a mouseup/touchend at the current position to release the pull
        const mouseUpEvent = new MouseEvent('mouseup', {
          bubbles: true,
          cancelable: true,
          view: window
        });
        scrollableDiv.dispatchEvent(mouseUpEvent);

        const touchEndEvent = new TouchEvent('touchend', {
          bubbles: true,
          cancelable: true,
          view: window,
          touches: [],
          targetTouches: [],
          changedTouches: []
        });
        scrollableDiv.dispatchEvent(touchEndEvent);

        isDraggingRef.current = false;
        
        // Force reset scroll position
        setTimeout(() => {
          if (scrollableDiv) {
            scrollableDiv.scrollTop = 0;
          }
        }, 0);
      }
    };

    // Listen for drag start
    scrollableDiv.addEventListener('mousedown', handleStart);
    scrollableDiv.addEventListener('touchstart', handleStart, { passive: true });
    
    // Listen for drag end
    scrollableDiv.addEventListener('mouseup', handleEnd);
    scrollableDiv.addEventListener('touchend', handleEnd, { passive: true });
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchend', handleEnd, { passive: true });
    
    // Listen for leaving the container
    scrollableDiv.addEventListener('mouseleave', handleLeave);
    scrollableDiv.addEventListener('touchcancel', handleLeave);

    return () => {
      scrollableDiv.removeEventListener('mousedown', handleStart);
      scrollableDiv.removeEventListener('touchstart', handleStart);
      scrollableDiv.removeEventListener('mouseup', handleEnd);
      scrollableDiv.removeEventListener('touchend', handleEnd);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchend', handleEnd);
      scrollableDiv.removeEventListener('mouseleave', handleLeave);
      scrollableDiv.removeEventListener('touchcancel', handleLeave);
    };
  }, []);

  return (
    <>
    {showEmptyPlaceholder ? <EmptyPlaceholder /> :
      <>
      <div id="scrollableDiv" ref={scrollableDivRef} className="scrollbar-style" style={{ height: "calc(100vh - 230px)", overflow: "auto" }}>
        <InfiniteScroll
          style={{ ...style }}
          dataLength={orderFilterData.length}
          next={() => getListData(false)}
          hasMore={orderFilterData.length < total}
          loader={<h4></h4>}
          scrollableTarget="scrollableDiv"
          refreshFunction={() => getListData(true)}
          pullDownToRefresh
          pullDownToRefreshThreshold={50}
          pullDownToRefreshContent={
            <h3 style={{ textAlign: 'center' }}>&#8595; Pull down to refresh</h3>
          }
          releaseToRefreshContent={
            <h3 style={{ textAlign: 'center' }}>&#8593; Release to refresh</h3>
          }
        >
          {isLoading && orderFilterData.length === 0 && <ChatsListSkeleton length={5} />}
          {(!isLoading || (isLoading && orderFilterData.length) >= 0) && orderFilterData.map((row, index) => {
            const localDate = row?.lastMessage?.createdOn ? moment(row.lastMessage.createdOn).local() : null;
            const chatUserId = row?.users && JSON.parse(row.users).find(x => x !== authId);
            const chatUser = chatUsers?.find(x => x.authId === chatUserId);

            const lastMessageLabel = messages["CHAT_LAST_MESSAGE"] || "";
            const timestampLabel = localDate
              ? `${lastMessageLabel}${moment(localDate).locale(locale).fromNow()}`
              : '';
            
            const styleClasses = row?.lastMessage 
              ? `chat-message-status-${row.lastMessage.status?.toLowerCase()} chat-side-${row.lastMessage.transmitterEntityNameId === authId ? 'peer' : 'user'}`
              : '';

            // Generate a unique key combining multiple identifiers
            const uniqueKey = `team-chat-${row.id}-${chatUserId}-${index}`;

            return (
              <React.Fragment key={uniqueKey}>
                <ChatsListItem
                  className={styleClasses}
                  variant="team"
                  leading={<AvatarChat user={chatUser} showStatusIcon={true} />}
                  title={chatUser?.label}
                  subtitle={row?.lastMessage ? <div style={{ display: "flex", gap: "4px", width: "100%" }}>
                    {row?.lastMessage?.transmitterEntityNameId === authId && row?.lastMessage?.status === 'Sent' && (
                      <Icon className="message-status-indicator">check</Icon>
                    )}
                    {row?.lastMessage?.transmitterEntityNameId === authId && row?.lastMessage?.status === 'Received' && (
                      <Icon className="message-status-indicator">done_all</Icon>
                    )}
                    {row?.lastMessage?.transmitterEntityNameId === authId && row?.lastMessage?.status === 'Read' && (
                      <Icon className="message-status-indicator message-status-indicator--read">done_all</Icon>
                    )}
                    <span className="chat-preview-subtitle-message">{row?.lastMessage?.message}</span>
                  </div> : null}
                  timestamp={timestampLabel}
                  onClick={() => handleOpenChat(row.id)}
                />
              </React.Fragment>
            );
          })}
        </InfiniteScroll>
      </div>
      <ChatThread
        messages={messages}
        locale={locale}
        openModal={selectedChat.openModal}
        isItemsLoading={selectedChat.loading}
        channel={selectedChat.channel}
        items={selectedChat.items}
        nextToken={selectedChat.nextToken}
        chatUsers={chatUsers}
        onCloseThread={handlecloseTeamChatList}
        onSendMessage={(payload) => dispatch(sendChatMessage(payload))}
        onUpdateMessageStatus={(ids, status) => dispatch(updateChatMessageStatus({ ids, status }))}
      />
      </>
    }
    </>
  );
};

export default TeamChatsList;
